import { AuthResponse } from "./auth/types";
import { EVENTS_CONFIG_BY_ENV } from "./auth/constants";
import { codeError, otpError, redirectUriErrors } from "./errors-translate";
import { ErrorObjForIterate, SdkServerError } from "./types";
import { MILLISECONDS_MULTIPLIER } from "./ui/UiToaster/constants";

/**
 * Removes trailing slashes from location origin
 * and returns the origin URL as a string
 *
 * @param origin
 */
export function getOriginFromUrl({ origin }: Location): string {
  return origin.replace(/\/+$/g, '');
}

/**
 * Determines the environment based on the authorization URL.
 * 
 * @param authUrl - Authorization URL
 * @returns Environment string (preprod, sandbox, dev, test, stage)
 */
export function getEnvironmentByAuthUrl(authUrl: string): string {
  const formattedAuthUrl = authUrl.replace(/\/$/, '');
  
  switch (formattedAuthUrl) {
    case 'https://preprod.gid-auth.ru':
      return 'preprod';
    case 'https://sandbox.gid-auth.ru':
      return 'sandbox';
    case 'https://dev-auth.k8s-dev.gid.team':
      return 'dev';
    case 'https://test-auth.k8s-dev.gid.team':
      return 'test';
    case 'https://stage-auth.k8s-dev.gid.team':
      return 'stage';
    default:
      return 'test';
  }
}

export const buildEventsConfig = (authUrl?: string): any => {
  const envName = authUrl ? getEnvironmentByAuthUrl(authUrl) : undefined;
  if (!envName || (envName === 'prod' || envName === 'preprod')) {
    return;
  }

  const currentConfig = EVENTS_CONFIG_BY_ENV[envName];
  return {
    trackingReceiver: {
      trackingReceiverUrl: currentConfig.trackingReceiverUrl,
      writeKey: currentConfig.writeKey,
    },
    goya: {
      goyaUrl: currentConfig.goyaUrl,
    },
    sentry: {
      sentryDsn: currentConfig.sentryDsn,
    },
    kevents: {
      keventsUrl: currentConfig.keventsUrl,
    },
    kfp: {
      kfpUrl: currentConfig.kfpPath,
    },
  };
};

export const getHeaders = (headers: unknown): {} | Record<string, string> => {
  if (!!headers && typeof headers === 'string') {
    try {
      const parsedHeaders = JSON.parse(headers);
      if (typeof parsedHeaders === 'object') {
        return parsedHeaders;
      } else {
        throw new Error();
      }
    } catch {
      console.log('Заголовки указаны некорректно', headers);
      console.log('Заголовки должны быть указаны в следующем формате: {"header1": "value1", "header2": "value2"}');
    }
  }
  return {};
}

/**
 * Encodes query parameters from an object-type parameter.
 *
 * @param {object} params
 */
export const createQueryParams = (params: Record<string, string>) => {
  return Object.keys(params)
    .filter(k => typeof params[k] !== 'undefined')
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
};

const isSdkServerError = (obj: any): obj is SdkServerError => {
  return (
    obj && typeof obj.error_message === 'string' && 
    typeof obj.error === 'string' && 
    typeof obj.status_code === 'number'
  )
}

export const logError = (error: unknown): void => {
  if (!error) console.log('The logError function is missing an argument')

  if (typeof error === 'string') {
    console.log(error)
  }

  if (isSdkServerError(error)) {
    const errorObj: ErrorObjForIterate = {
      error: error?.error || '',
      status_code: error?.status_code || null,
      error_message: error?.error_message || '',
      retry_after_seconds: error?.timeout || undefined,
    }
  
    const filteredObj = Object.keys(errorObj).reduce((acc: ErrorObjForIterate, key) => {
      if (errorObj[key] !== undefined) {
        acc[key] = errorObj[key];
      }
      return acc;
    }, {});

    console.log('Ошибка с бэкенда', filteredObj)
  }

  console.log('Произошла непредвиденная ошибка. Обратитесь в службу поддержки')
}

/**
 * Writes the current time plus timeout seconds
 * as a timestamp to session storage
 *
 * @param timeout
 */
export const setOtpTimeout = (timeout: number): void => {
  const timeoutSeconds = timeout * MILLISECONDS_MULTIPLIER;
  const value = Date.now() + timeoutSeconds;

  window.sessionStorage.setItem('timeout', `${value}`);
};

/**
 * Determines the appropriate form of a word based on the provided number.
 * 
 * @param number The number based on which the form is determined.
 * @param one The form of the word to use for exactly one item.
 * @param few The form of the word to use for a few items (typically 2-4).
 * @param many The form of the word to use for many items (5 and above, including teens).
 * @returns The appropriate word form as a string.
 */
export const pluralize = (number: number, one: string, few: string, many: string): string => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

/**
 * Returns the normalized instance of Error constructor
 * with the fallback message for unknown error instances
 *
 * @param error
 */
export const normalizedError = (error: unknown): Error => {
  let message = 'Произошла непредвиденная ошибка. Обратитесь в службу поддержки';

  if (error instanceof Error && 'error_description' in error && 'error' in error) {
    const sdkError = error as Error & { error: string; error_description: string };
    message = sdkError.error_description;
  } else if (error instanceof Error && 'error_message' in error) {
    switch (error.error_message) {
      case (redirectUriErrors):
        message = 'Произошла непредвиденная ошибка. Обратитесь в службу поддержки!'
        break
      case (otpError):
      case (codeError):
          message = 'Неверный код'
          break
      default:
        message = `${error.error_message}`;
    }
  }

  if (error instanceof Error && 'error_message' in error && ('retry_after_seconds' in error || 'timeout' in error)) {
    const retryAfterSeconds = 'retry_after_seconds' in error
      ? error.retry_after_seconds
      : error.timeout;
    const secondsText = retryAfterSeconds
      ? pluralize(Number(retryAfterSeconds), 'секунду', 'секунды', 'секунд')
      : '';
    message = `${error.error_message} ${secondsText ? retryAfterSeconds + ` ${secondsText}` : ''}`;
  }

  if (typeof error === 'string') {
    message = error
  }

  return new Error(message);
};

/**
 * Extracts the authorization params
 * (code and state) from URL query
 *
 * @param query
 */
export const parseAuthResponse = (
  query = '',
): AuthResponse => {
  if (query.indexOf('#') > -1) {
    query = query.slice(0, query.indexOf('#'));
  }

  const { searchParams } = new URL(query);

  return {
    state: searchParams.get('state') || '',
    code: searchParams.get('code') || '',
    ksid: searchParams.get('ksid') || undefined,
  };
};

/**
 * Returns the number of seconds remaining to the timeout
 * from session storage. If timeout point has been passed,
 * will return zero.
 */
export const getOtpTimeout = (): number => {
  const savedTimeout = window.sessionStorage.getItem('timeout') || '';
  const timeout = Number.parseInt(savedTimeout, 10);
  const remaining = timeout - Date.now();

  return remaining > 0
    ? Math.floor(remaining / MILLISECONDS_MULTIPLIER)
    : 0;
};